// @copyright  2006-2026 West Indies Software Engineering Company Limited. All rights reserved.

export default {
    '$dir': 'ltr',

    'practiceManagement.clients.title': 'Clients',
    'practiceManagement.clients.empty': 'No clients yet. Invite your first client to get started.',
    'practiceManagement.clients.inviteAction': 'Invite client',
    'practiceManagement.clients.reviewAction': 'Review and set up',
    'practiceManagement.clients.state.invited': 'Invited',
    'practiceManagement.clients.state.profileSubmitted': 'Awaiting your review',
    'practiceManagement.clients.state.active': 'Active',
    'practiceManagement.clients.state.terminated': 'Ended',
    'practiceManagement.clients.state.revoked': 'Withdrawn',
    'practiceManagement.clients.error': 'We could not load your clients. Try again.',
    'practiceManagement.clients.denied': 'You do not have access to the client list.',

    'practiceManagement.clients.loading': 'Loading your clients…',
    'practiceManagement.clients.retry': 'Try again',
    'practiceManagement.clients.filterLabel': 'Engagement state',
    'practiceManagement.clients.filterAll': 'All states',
    // The `column.*`, `awaitingName` and `openAction` entries were removed on
    // 2026-08-11 with the clients table they labelled: the list is now the
    // master column of the master–detail frame, and a catalogue entry nothing
    // renders is a string a translator pays for twice.
    'practiceManagement.clients.payer.practice': 'Our practice',
    'practiceManagement.clients.payer.client': 'The client',
    'practiceManagement.clients.unnamed': 'Client not yet identified',
    'practiceManagement.clients.noServices': 'None recorded',

    'practiceManagement.clients.all': 'All clients',
    'practiceManagement.clients.searchResults': 'Search results',
    'practiceManagement.clients.searchTitle': 'Search clients',
    'practiceManagement.clients.searchAction': 'Search',
    'practiceManagement.clients.searchReset': 'Reset',
    'practiceManagement.clients.clearSearch': 'Clear search',
    'practiceManagement.clients.searchEmpty': 'No clients match your search.',
    'practiceManagement.clients.nameFilterLabel': 'Client name',
    'practiceManagement.clients.nameFilterHelp': 'Matches against the loaded list of clients.',
    'practiceManagement.clients.searchNameBound': 'Only the clients loaded on this page are searched by name.',
    // `startTitle` and `startHelp` were removed on 2026-08-11 with the
    // "select a client" prose panel they captioned: the unselected content
    // area now shows the HeadOffice logo, as the invoices and loans screens
    // do, and a catalogue entry nothing renders is a string a translator
    // pays for twice.
    // Natural phrases, not "Category: figure" (user refinement, 2026-08-11 —
    // the colon prefixes were dropped when the hint moved above the state
    // chip). The noun stays inside each phrase so the four hints remain
    // tellable apart without their prefixes, and the counted three are
    // pipe-pluralised ($tc) — the first cut rendered "1 tasks overdue".
    'practiceManagement.clients.hint.returnsOverdue': '{count} return overdue | {count} returns overdue',
    'practiceManagement.clients.hint.returnsDue': '{count} return due | {count} returns due',
    'practiceManagement.clients.hint.tasksOverdue': '{count} task overdue | {count} tasks overdue',
    'practiceManagement.clients.hint.bankUnreconciled': '{count} unreconciled',
    'practiceManagement.clients.hintsFailed': 'We could not load the compliance hints for your clients.',
    'practiceManagement.clients.service.bookkeeping': 'Bookkeeping',
    'practiceManagement.clients.service.tax': 'Tax',
    'practiceManagement.clients.service.payroll': 'Payroll',
    'practiceManagement.clients.service.audit': 'Audit',
    'practiceManagement.clients.service.advisory': 'Advisory',

    'practiceManagement.invite.title': 'Invite a client',
    'practiceManagement.invite.emailLabel': "Client's e-mail address",
    'practiceManagement.invite.payerLabel': "Who pays for this client's subscription?",
    'practiceManagement.invite.payerClient': 'The client pays',
    'practiceManagement.invite.payerPractice': 'Our practice pays',
    'practiceManagement.invite.payerImmutable': 'This cannot be changed later.',
    'practiceManagement.invite.servicesLabel': 'Services you will provide',
    'practiceManagement.invite.submit': 'Send invitation',
    'practiceManagement.invite.success': 'Invitation sent to {email}.',
    'practiceManagement.invite.duplicate': 'This client already has an invitation outstanding. Withdraw it first if you want to send a new one.',

    // WITHDRAWING AN INTAKE INVITATION (user ruling, 2026-08-14). The advice in
    // the line above has been on screen since the module shipped, for an action
    // nothing offered: `RevokeClientInvitationProcess` existed, the API module
    // had a `revoke()` method, and no control called either. A practice who
    // invited the wrong address had no way out of it.
    //
    // The help line is about the ENGAGEMENT rather than the e-mail, because that
    // is the part a practice user would not predict: the Process closes the
    // client relationship the invitation opened, in the same transaction.
    'practiceManagement.clients.withdrawInvitation': 'Withdraw invitation',
    'practiceManagement.clients.withdrawInvitationNamed': 'Withdraw the invitation sent to {email}',
    'practiceManagement.clients.withdrawInvitationConfirm': 'Withdraw the invitation sent to {email}?',
    'practiceManagement.clients.withdrawInvitationHelp': 'The link stops working and this client is closed. You can invite the same address again afterwards.',
    'practiceManagement.clients.withdrawInvitationReasonLabel': 'Why are you withdrawing it?',
    'practiceManagement.clients.withdrawInvitationReasonHelp': 'Optional. This is kept with the invitation.',
    'practiceManagement.clients.withdrawInvitationAction': 'Withdraw invitation',
    'practiceManagement.clients.invitationWithdrawn': 'The invitation to {email} has been withdrawn.',
    'practiceManagement.clients.withdrawInvitationFailed': 'We could not withdraw the invitation. Try again.',
    'practiceManagement.clients.invitationOutstanding': 'Invitation sent to {email}, not yet accepted.',
    'practiceManagement.invite.error': 'We could not send the invitation. Try again.',

    'practiceManagement.invite.payerClientHelp': 'They will be asked for card details when they complete their profile. You cannot set up their organisation until they have provided them.',
    'practiceManagement.invite.payerPracticeHelp': "This client's subscription will be charged to your practice's payment method.",
    'practiceManagement.invite.payerImmutableHelp': 'To switch, you would need to withdraw the invitation and send a new one.',
    'practiceManagement.invite.payerRequired': 'Choose who pays before sending the invitation.',
    'practiceManagement.invite.servicesHelp': 'Optional. You can change these later.',
    'practiceManagement.invite.cancel': 'Cancel',

    'practiceManagement.review.title': 'Review and set up',
    'practiceManagement.review.submittedDetails': 'What your client told us',
    'practiceManagement.review.planLabel': 'Subscription plan',
    'practiceManagement.review.payerPractice': 'Charged to your practice',
    'practiceManagement.review.payerClient': 'Charged to the client',
    'practiceManagement.review.blockedNoPaymentMethod': 'Waiting for payment details',
    'practiceManagement.review.blockedOutstanding': '{count} details are still needed',
    'practiceManagement.review.blockedOutstandingHelp': 'Your client left these blank, and their organisation cannot be set up without them:',
    'practiceManagement.review.goToDetails': 'Go to the details',
    'practiceManagement.review.outstandingIndustry': 'What the business does',
    'practiceManagement.review.outstandingRegistration': 'TRN and NIS number',
    'practiceManagement.review.fromClient': 'From your client',
    'practiceManagement.review.fromPractice': 'Your entry',
    'practiceManagement.review.notSupplied': 'Not supplied',
    'practiceManagement.review.clientSent': 'Your client sent: {value}',
    'practiceManagement.review.edit': 'Edit',
    'practiceManagement.review.add': 'Add',
    'practiceManagement.review.revert': 'Revert',
    'practiceManagement.review.tooLongForSetUp': 'This is longer than an organisation name can be. Shorten it to continue.',
    'practiceManagement.review.confirmTitle': 'Set up {client}?',
    'practiceManagement.review.confirmIrreversible': 'This cannot be undone from within HeadOffice.',
    'practiceManagement.review.provisioning': 'Setting up their organisation…',
    'practiceManagement.review.gatewayFailure': 'Payment could not be taken: {message}',

    'practiceManagement.review.loading': 'Loading this client…',
    'practiceManagement.review.error': 'We could not load this client. Try again.',
    'practiceManagement.review.denied': 'You do not have access to this client.',
    'practiceManagement.review.retry': 'Try again',
    'practiceManagement.review.blockedOutstandingOne': 'One detail is still needed',
    'practiceManagement.review.blockedPracticeBillingMethod': 'Your practice has no payment method',
    'practiceManagement.review.blockedPracticeBillingMethodHelp': "Your practice is paying for this client's subscription, but your practice has no payment method on file. Add one in your subscription settings, then come back here.",
    'practiceManagement.review.blockedNoPaymentMethodHelp':
        'This client is paying for their own subscription, but they have not yet provided card details. Ask them to complete their profile using the link they were sent, then come back here.',
    'practiceManagement.review.outstandingBillingMethod': "The client's card details",
    'practiceManagement.review.outstandingPracticeBillingMethod': "Your practice's payment method",
    'practiceManagement.review.save': 'Save',
    'practiceManagement.review.cancelEdit': 'Cancel',
    'practiceManagement.review.charactersUsed': '{count} of {max} characters',
    'practiceManagement.review.tooLongForField': 'This is longer than {max} characters. Shorten it to continue.',
    'practiceManagement.review.invalidEmail': 'Enter a valid e-mail address.',
    'practiceManagement.review.field.legalName': 'Registered business name',
    'practiceManagement.review.field.tradingName': 'Trading name',
    'practiceManagement.review.field.contactName': 'Contact name',
    'practiceManagement.review.field.contactEmail': 'Contact e-mail',
    'practiceManagement.review.field.contactTelephone': 'Contact telephone',
    'practiceManagement.review.field.jurisdiction': 'Where registered',
    'practiceManagement.review.field.entityType': 'Type of business',
    'practiceManagement.review.field.financialYearEnd': 'Financial year end',
    'practiceManagement.review.field.industryType': 'What the business does',
    'practiceManagement.review.registrationHeading': 'Registration numbers',
    'practiceManagement.review.registrationNotReturned': 'We cannot show what your client entered here, so anything you type is recorded as your entry. Leave a box empty to use your client’s own answer.',
    'practiceManagement.review.industryNotReturned': 'We cannot show what your client entered here. Leave this alone to use your client’s own answer.',
    'practiceManagement.review.setUpHeading': 'Set up their organisation',
    'practiceManagement.review.priceLabel': 'Billing cycle',
    'practiceManagement.review.noPlanForJurisdiction': 'We have no subscription plan for {jurisdiction}, so this organisation cannot be set up yet.',
    'practiceManagement.review.provisionAction': 'Set up organisation',
    'practiceManagement.review.cancel': 'Cancel',
    'practiceManagement.review.confirmBody': 'This creates their organisation and starts their subscription on the {plan} plan at {price}, {payer}.',
    'practiceManagement.review.confirmJointOwnership': 'You and your client will both own their organisation.',
    'practiceManagement.review.confirmSoleOwnership': 'Your client has not linked an account, so you will be the only owner of their organisation.',
    'practiceManagement.review.confirmPayerPractice': 'charged to your practice',
    'practiceManagement.review.confirmPayerClient': 'charged to the client',
    'practiceManagement.review.alreadyProvisioned': 'This client already has an organisation.',
    'practiceManagement.review.openEngagement': 'Open this client',
    'practiceManagement.review.notProvisionable': 'This client is {state}, so there is nothing to set up here.',
    'practiceManagement.review.provisionFailed': 'We could not set up this organisation. Check the client list before trying again — the organisation may already have been created.',
    'practiceManagement.review.navigationWarning': 'Setting up an organisation is still in progress. Leaving now means you will have to check the client list to find out whether it finished.',
};
